import { useEffect, useState } from "react";
import { Crown, Shield, MapPin, Phone, User, Bell, BellOff, Users, Mail } from "lucide-react";
import { b64ToSrc } from "@/lib/img";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Publisher identity badge displayed on EventDetail when the author opted
 * to publish under their real identity (admin) or chose to expose
 * name/address/phone (pro account). Includes a "Follow this organizer"
 * button that listens for new publications.
 */
export default function PublisherCard({ card }) {
    const { user } = useAuth();
    const [followers, setFollowers] = useState(0);
    const [following, setFollowing] = useState(false);
    const [loading, setLoading] = useState(false);
    const oid = card?.organizer_id;
    const isMine = user && oid === user.id;

    useEffect(() => {
        if (!user || !oid) return;
        let cancelled = false;
        api.get(`/organizers/${oid}/follow-status`).then(r => {
            if (cancelled) return;
            setFollowers(r.data?.followers || 0);
            setFollowing(!!r.data?.following);
        }).catch(() => { /* ignore */ });
        return () => { cancelled = true; };
    }, [user, oid]);

    const toggleFollow = async () => {
        if (!user) { toast.info("Connectez-vous pour suivre"); return; }
        setLoading(true);
        try {
            const verb = following ? "delete" : "post";
            const r = await api[verb](`/organizers/${oid}/follow`);
            setFollowing(!!r.data?.following);
            setFollowers(r.data?.followers || 0);
            toast.success(r.data?.following
                ? "Vous suivez cet organisateur — vous serez notifié de ses prochains événements."
                : "Vous ne suivez plus cet organisateur.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
        setLoading(false);
    };

    if (!card) return null;
    const isAdmin = card.kind === "admin";
    const Icon = isAdmin ? Shield : Crown;
    const photoSrc = b64ToSrc(card.photo_base64);
    const hasContent = card.name || card.address || card.phone || card.city || card.email;
    if (!hasContent) return null;
    return (
        <div className="discover-card p-4 space-y-3" data-testid="publisher-card">
            <div className="flex items-center gap-3">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden ${isAdmin ? "bg-blue-500" : "bg-discover-gradient"}`}>
                    {photoSrc ? (
                        <img src={photoSrc} alt={card.name || "logo"}
                            className="w-full h-full object-cover"
                            data-testid="publisher-card-photo" />
                    ) : (
                        <Icon size={26} className="text-white" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isAdmin ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                            {isAdmin ? "Équipe Discoveur" : "Organisateur Pro"}
                        </span>
                        {card.name && (
                            <span className="font-bold flex items-center gap-1"
                                data-testid="publisher-card-name">
                                <User size={12} />{card.name}
                            </span>
                        )}
                    </div>
                    {(card.address || card.city) && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"
                            data-testid="publisher-card-address">
                            <MapPin size={12} />
                            {[card.address, card.city].filter(Boolean).join(", ")}
                        </p>
                    )}
                    {card.phone && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"
                            data-testid="publisher-card-phone">
                            <Phone size={12} />
                            <a href={`tel:${card.phone}`} className="text-primary hover:underline">{card.phone}</a>
                        </p>
                    )}
                    {card.email && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"
                            data-testid="publisher-card-email">
                            <Mail size={12} />
                            <a href={`mailto:${card.email}`} className="text-primary hover:underline">{card.email}</a>
                        </p>
                    )}
                </div>
            </div>
            {oid && !isMine && (
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground flex items-center gap-1"
                        data-testid="publisher-card-followers">
                        <Users size={12} />
                        {followers} {followers === 1 ? "abonné" : "abonnés"}
                    </span>
                    <Button onClick={toggleFollow} disabled={loading || !user}
                        size="sm"
                        variant={following ? "outline" : "default"}
                        className={`rounded-full text-xs ${following ? "" : "bg-discover-gradient text-white"}`}
                        data-testid="publisher-card-follow-btn">
                        {following ? (
                            <><BellOff size={12} className="mr-1" />Ne plus suivre</>
                        ) : (
                            <><Bell size={12} className="mr-1" />Suivre cet organisateur</>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}
